import { createColumnHelper } from "@tanstack/react-table";
import { endOfDay, isAfter, parseISO } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Label } from "semantic-ui-react";

import { Can, DATE_FORMATS_FNS, ReactTable } from "components";
import { usePermanentRealTimeUser, useRegisterNavAction } from "hooks";
import { DEALER_DOCUMENTATION_CATEGORIES } from "models";
import { DealerDocumentationReadingModal } from "modules/DealerDocumentations/components";
import "modules/DealerDocumentations/components/DealerDocumentationTable/DealerDocumentationTable.scss";
import { useDealerDocumentationsFilters, useDealersDocumentationsQuery, useEntityNames } from "modules/DealerDocumentations/hooks";
import { enumToDropdownOptions } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { DealerDocumentation } from "models";

type DealerDocumentationTableProps = {
  handleSetShowAddEditDealerDocumentationModal: (value: DealerDocumentation | null) => void;
};

const columnHelper = createColumnHelper<DealerDocumentation>();

export const DealersDocumentationTable = ({ handleSetShowAddEditDealerDocumentationModal }: DealerDocumentationTableProps) => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { dealer_ids, dealer_location_ids, category_id } = useDealerDocumentationsFilters();
  const { data: dealersDocumentations, refetch: refetchDealersDocumentations, isFetching } = useDealersDocumentationsQuery();
  const { matchDealersAndLocations } = useEntityNames();

  useRegisterNavAction(() => (dealer_ids.length > 0 || dealer_location_ids.length > 0) && refetchDealersDocumentations());

  const [readingDealerDocumentationModal, setReadingDealerDocumentationModal] = useState<DealerDocumentation | null>(null);

  const filteredDealersDocumentations = (category_id ? dealersDocumentations?.filter(doc => doc.category_id === category_id) : dealersDocumentations) ?? [];
  const categoryOptions = enumToDropdownOptions({ enumObj: DEALER_DOCUMENTATION_CATEGORIES, t });
  const getCategoryName = (id?: number) => (id ? categoryOptions.find(category => category.value === id)?.text : "");

  const handleCloseReadingDealerDocumentationModal = () => setReadingDealerDocumentationModal(null);

  const checkPublishDate = (publish_date: string | Date | undefined) => {
    if (!publish_date) return false;

    const dateToCheck = typeof publish_date === "string" ? parseISO(publish_date) : publish_date;

    return isAfter(dateToCheck, endOfDay(new Date()));
  };

  const isOwnerOrHasHigherPrivilege = (data: DealerDocumentation) => {
    if (!user) return false;

    if (data.publish_date === null || checkPublishDate(data.publish_date)) return user.id === data.created_by?.id;

    if (!data.created_by?.role_id) return false;
    return user.role_id <= data.created_by.role_id;
  };

  const columns = [
    columnHelper.accessor(row => row, {
      id: "title",
      header: t("v8_title", "Title"),
      cell: ({
        row: {
          original: { title, is_pinned }
        }
      }) => (
        <>
          <Icon className={is_pinned ? "pin" : "circle regular"} /> {title}
        </>
      ),
      size: 200
    }),
    columnHelper.accessor(row => row, {
      id: "dealer_locations",
      header: t("v8_dealers_&_locations", "Dealers/Locations"),
      cell: ({
        row: {
          original: { dealer_ids, dealer_location_ids }
        }
      }) => matchDealersAndLocations(dealer_ids ?? [], dealer_location_ids ?? []),
      size: 150
    }),

    columnHelper.accessor(row => row, {
      id: "publish_date",
      header: t("v8_publish_date", "Publish Date"),
      cell: ({
        row: {
          original: { publish_date }
        }
      }) => <span> {publish_date ? formatDate(publish_date, DATE_FORMATS_FNS.dateMonthYear) : ""}</span>,
      size: 100
    }),
    columnHelper.accessor(row => row, {
      id: "category",
      header: t("v8_category", "Category"),
      cell: ({
        row: {
          original: { category_id }
        }
      }) => <Label className="doc-table-category"> {getCategoryName(category_id)} </Label>,
      size: 80
    }),
    columnHelper.accessor(row => row, {
      id: "published_by",
      header: t("v8_published_by", "Published By"),
      cell: ({
        row: {
          original: { updated_by, created_by }
        }
      }) => {
        const publishedBy = updated_by ? `${updated_by.first_name} ${updated_by.last_name}` : `${created_by?.first_name} ${created_by?.last_name}`;
        return <span> {publishedBy} </span>;
      },
      size: 100
    }),

    columnHelper.accessor(row => row, {
      id: "edit",
      header: "",
      cell: ({ row: { original } }) => {
        if (!isOwnerOrHasHigherPrivilege(original)) return null;

        return (
          <Can I="update" the="dealer-documentation">
            <div className="edit-button-container">
              <div
                className="edit-button"
                onClick={e => {
                  e.stopPropagation();
                  handleSetShowAddEditDealerDocumentationModal(original);
                }}
              >
                <Icon className="pen to square edit-icon" />
              </div>
            </div>
          </Can>
        );
      },
      size: 50
    })
  ];

  return (
    <div className="DealerDocumentationTable">
      <ReactTable isFetching={isFetching} columns={columns} data={filteredDealersDocumentations} onRowClick={setReadingDealerDocumentationModal} />
      <DealerDocumentationReadingModal documentation={readingDealerDocumentationModal} handleCloseViewModalDocumentation={handleCloseReadingDealerDocumentationModal} />
    </div>
  );
};
